import { z } from 'zod';
import { SENTIMENT_ADAPTER_KEYS, SENTIMENT_DIRECTIONS } from '../enums';
import { decimalInRange, decimalStringSchema, jsonObjectSchema, uuidSchema } from './common';

/** Sentiment providers and events (API_DOCS.md — Sentiment Providers). */

/** Adapter output score is -1..1; confidence 0..1. Both are decimal strings. */
export const scoreSchema = decimalStringSchema.refine(
  decimalInRange('-1', '1'),
  'must be between -1 and 1',
);
export const confidenceSchema = decimalStringSchema.refine(
  decimalInRange('0', '1'),
  'must be between 0 and 1',
);

export const sentimentProviderSchema = z.object({
  key: z.enum(SENTIMENT_ADAPTER_KEYS),
  enabled: z.boolean(),
  weight: decimalStringSchema,
  healthy: z.boolean().nullable(),
  lastLatencyMs: z.number().int().nullable(),
  callsToday: z.number().int().nonnegative(),
  costEstimateUsd: decimalStringSchema,
  /** null = free adapter (regex). */
  costPerCallUsd: decimalStringSchema.nullable(),
  credentialRequired: z.boolean(),
});
export type SentimentProviderDto = z.infer<typeof sentimentProviderSchema>;

export const sentimentProviderUpdateSchema = z
  .object({
    enabled: z.boolean().optional(),
    weight: decimalStringSchema.optional(),
    params: jsonObjectSchema.optional(),
  })
  .refine((v) => Object.keys(v).length > 0, 'at least one field must be provided');
export type SentimentProviderUpdate = z.infer<typeof sentimentProviderUpdateSchema>;

export const sentimentEventDtoSchema = z.object({
  id: uuidSchema,
  adapter: z.enum(SENTIMENT_ADAPTER_KEYS),
  symbol: z.string(),
  score: scoreSchema,
  confidence: confidenceSchema,
  direction: z.enum(SENTIMENT_DIRECTIONS),
  rationale: z.string().nullable(),
  newsTitle: z.string().nullable(),
  latencyMs: z.number().int().nullable(),
  createdAt: z.string().datetime(),
});
export type SentimentEventDto = z.infer<typeof sentimentEventDtoSchema>;

export const sentimentEventQuerySchema = z.object({
  symbol: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type SentimentEventQuery = z.infer<typeof sentimentEventQuerySchema>;

export const sentimentTestRequestSchema = z.object({
  text: z.string().min(1).max(4000),
});
export type SentimentTestRequest = z.infer<typeof sentimentTestRequestSchema>;

export const sentimentTestResponseSchema = z.object({
  results: z.array(
    z.object({
      adapter: z.enum(SENTIMENT_ADAPTER_KEYS),
      score: scoreSchema,
      confidence: confidenceSchema,
      direction: z.enum(SENTIMENT_DIRECTIONS),
      rationale: z.string().nullable(),
      latencyMs: z.number().int(),
    }),
  ),
});
export type SentimentTestResponse = z.infer<typeof sentimentTestResponseSchema>;
